import { useState, type ReactNode } from 'react';
import { IconChartBar, IconBell, IconChecklist, IconX } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button } from '@hana/ui';
import { QUOTE_NOTIS, QUOTE_TASKS } from './fixtures';
import { QUOTE_STATUSES, quoteStats, statusOf } from './logic';
import type { Quote, QuoteStatus } from './types';
import './quoteDock.i18n';
import s from './quotes.module.css';

// Bảng phải 372 của Mint-Quote-1920 (mở sẵn): hàng chờ hỏi giá (slot) · tab Phân tích / Việc / Thông báo. Không GP% (chỉ sau quyền money).

export const STATUS_DOT: Record<QuoteStatus, string> = {
  'Nháp': 'var(--d-gray)', 'Đã gửi': 'var(--d-blue)', 'Khách chấp nhận': 'var(--d-green)', 'Hết hạn': 'var(--d-amber)', 'Đã thành Job': 'var(--d-violet)',
};
type Tab = 'an' | 'task' | 'noti';
const TABS: readonly { key: Tab; label: string; n: number; Icon: typeof IconChartBar }[] = [
  { key: 'an', label: 'qd.tab.an', n: 0, Icon: IconChartBar },
  { key: 'task', label: 'qd.tab.task', n: 4, Icon: IconChecklist },
  { key: 'noti', label: 'qd.tab.noti', n: 3, Icon: IconBell },
];
const TONE = { err: s.err, warn: s.warnTx, lb: s.lb } as const;
const NOTI_DOT = { info: 'var(--link)', warn: 'var(--d-amber)', read: 'var(--bd)' } as const;

export function QuoteDock({ rows, inquiries, onClose }: { rows: readonly Quote[]; inquiries: ReactNode; onClose: () => void }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>('an');
  const [done, setDone] = useState<Set<number>>(new Set());
  const st = quoteStats(rows);
  const seg = QUOTE_STATUSES.map((k) => ({ k, n: rows.filter((r) => statusOf(r) === k).length }));
  const exp = rows.filter((r) => r.validDays >= 0 && r.validDays <= 7).sort((a, b) => a.validDays - b.validDays);
  const arc = 251.3 * (1 - st.acceptRate / 100);
  const toggle = (i: number) => setDone((d) => { const n = new Set(d); if (!n.delete(i)) n.add(i); return n; });
  return (
    <div className={s.dock}>
      {inquiries}
      <div className={s.dkBar}>
        <div className={s.dkTabs} role="tablist" aria-label={t('qd.tabs')}>
          {TABS.map((tb) => (
            <button key={tb.key} type="button" role="tab" aria-selected={tab === tb.key} onClick={() => setTab(tb.key)}>
              {t(tb.label)}
              {tb.n > 0 && <span className={`${s.dkN} ${tb.key === 'noti' ? s.dkNr : ''}`}>{tb.n}</span>}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" aria-label={t('qd.close')} title={t('qd.close')} icon={<IconX size={17} aria-hidden="true" />} onClick={onClose} />
      </div>
      {tab === 'an' && (
        <>
          <section className={s.dkCard}>
            <h2>{t('qd.month')}</h2>
            <div className={s.dkGauge}>
              <svg role="img" aria-label={t('qd.gaugeAria', { r: st.acceptRate, a: st.accepted, s: st.sent })} width="150" height="82" viewBox="0 0 190 104">
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
                <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--ok-dot)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={arc} />
                <text x="95" y="82" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)" fontFamily="inherit">{st.acceptRate}%</text>
              </svg>
              <div>
                <div className={s.lb}>{t('qd.rate')}</div>
                <b>{t('qd.sentQ', { a: st.accepted, s: st.sent })}</b>
                <div className={s.lb}>{t('qd.acc')}</div>
              </div>
            </div>
            <div className={s.dkKpi}>
              <div><div className={s.lb}>{t('qd.sent')}</div><b>{st.sent}</b> <span className={s.lb}>{t('qd.qt')}</span></div>
              <div><div className={s.lb}>{t('qd.exp')}</div><b className={s.warnTx}>{st.expiringSoon}</b> <span className={s.lb}>{t('qd.qt')}</span></div>
            </div>
          </section>
          <section className={s.dkCard}>
            <h2>{t('qd.mix')}</h2>
            <div className={s.dkBarSeg} role="img" aria-label={t('qd.mixAria', { v: seg.map((x) => `${x.k} ${x.n}`).join(', ') })}>
              {seg.filter((x) => x.n > 0).map((x) => <div key={x.k} style={{ flex: `${x.n} 1 0`, background: STATUS_DOT[x.k] }} />)}
            </div>
            <div className={s.dkLegend}>
              {seg.map((x) => (
                <span key={x.k}><i style={{ background: STATUS_DOT[x.k] }} aria-hidden="true" /><span>{x.k}</span><b>{x.n}</b></span>
              ))}
            </div>
          </section>
          <section className={s.dkCard}>
            <h2>{t('qd.expH')}</h2>
            {exp.map((r) => {
              const left = r.validDays === 0 ? t('qd.today') : t('qd.left', { n: r.validDays });
              return (
                <div key={r.id} className={s.dkExp}>
                  <span className={s.dkNo}>{r.id}</span>
                  <span className={s.dkCus}>{r.customer}</span>
                  <span className={s.dkTicks} role="img" aria-label={`${r.id} ${left}`}>
                    {Array.from({ length: 7 }, (_, j) => <span key={j} style={{ background: j < r.validDays ? 'var(--d-amber)' : 'var(--line)' }} />)}
                  </span>
                  <span className={s.dkLeft}>{left}</span>
                </div>
              );
            })}
          </section>
        </>
      )}
      {tab === 'task' &&
        QUOTE_TASKS.map((k, i) => (
          <div key={k.k} className={s.dkTask} style={{ opacity: done.has(i) ? 0.5 : 1 }}>
            <input type="checkbox" className={s.check} aria-label={t('qd.done', { k: k.k })} checked={done.has(i)} onChange={() => toggle(i)} />
            <div>
              <div style={{ textDecoration: done.has(i) ? 'line-through' : 'none' }}>{k.k}</div>
              <div className={s.lb}><span className={s.dkCode}>{k.code}</span> <span className={TONE[k.tone]}>{k.due}</span></div>
            </div>
          </div>
        ))}
      {tab === 'noti' &&
        QUOTE_NOTIS.map((n) => (
          <div key={n.k} className={s.dkNoti}>
            <span className={s.dkDot} style={{ background: NOTI_DOT[n.tone] }} aria-hidden="true" />
            <div><div style={{ fontWeight: n.unread ? 600 : 400 }}>{n.k}</div><div className={s.lb}>{n.when}</div></div>
          </div>
        ))}
    </div>
  );
}
